import axios from 'axios';
import { TOKEN_KEY, USER_KEY } from '../CONSTANTS';

const axiosConfig = axios.create({
    //baseURL: API_BASE_URL || 'http://localhost:3000'
});

// Access tokens last minutes, not months. The server keeps the long-lived
// refresh token in an httpOnly cookie that script can't read; this asks it for
// a new access token (and the cookie is replaced as a side effect). One request
// at a time: several calls failing together share a single refresh, because the
// refresh token is single-use.
let refreshing = null;
export const refreshAccessToken = () => {
    if (!refreshing) {
        refreshing = axios
            .post('/auth/token/refresh/', {})
            .then((response) => {
                localStorage.setItem(TOKEN_KEY, response.data.access);
                return response.data.access;
            })
            .finally(() => {
                refreshing = null;
            });
    }
    return refreshing;
};

// The app is signed out (no token, or the server won't renew it). Clears the
// stored sign-in and tells App.js, which flips to its logged-out state; that
// sends the person to /login from whichever page they were on, remembering it
// so they come straight back after signing in (PrivateRoute passes `from`).
export const SIGNED_OUT_EVENT = 'planpraise:signed-out';

const signedOut = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    window.dispatchEvent(new Event(SIGNED_OUT_EVENT));
};

axiosConfig.interceptors.request.use(request => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
        request.headers.common['Authorization'] = `Bearer ${token}`;
    }
    return request;
}, Promise.reject);

// Add a response interceptor to catch errors and take action
axiosConfig.interceptors.response.use(response => {
    // Any status code that lie within the range of 2xx cause this function to trigger
    return response;
}, error => {
    const { config, response } = error;
    // Only an expired/invalid sign-in is worth reacting to; anything else
    // (validation errors, a flaky connection) goes back to the caller as is.
    if (!response || response.status !== 401 || !config || config._retried) {
        return Promise.reject(error);
    }
    // Already on /login - nothing to refresh or redirect from.
    if (window.location.pathname === '/login') {
        return Promise.reject(error);
    }
    // The app believes it's signed in but there is no token to renew (storage
    // was cleared, or another tab signed out): that is just being signed out.
    // Leaving it as a plain failed request made pages report it as a missing
    // item (an event link read "Can't find this event").
    if (!localStorage.getItem(TOKEN_KEY)) {
        signedOut();
        return Promise.reject(error);
    }
    config._retried = true;
    return refreshAccessToken().then(
        (token) => {
            config.headers['Authorization'] = `Bearer ${token}`;
            return axiosConfig(config);
        },
        (refreshError) => {
            // The refresh token is gone, expired or revoked: sign in again.
            if (refreshError.response) signedOut();
            return Promise.reject(error);
        }
    );
});

export default axiosConfig;
